/** The generator panel: settings, the run itself, and JSON save / open. */

import { getBaseDatumMm } from "../../data/haki.ts";
import type { TerrainKind } from "../../engine/terrain.ts";
import { download } from "../../lib/download.ts";
import { fail, ok, type Result } from "../../lib/result.ts";
import type { DeckMode } from "../../model/generator.ts";
import type { PlankFamily } from "../../model/solver.ts";
import { setBaseDatum } from "../datum/datum.actions.ts";
import { getEngine } from "../engine/engine.actions.ts";
import { setHud } from "../hud/hud.actions.ts";
import { restorePlanes, serializePlanes } from "../planes/planes.actions.ts";
import { type GeneratorState, generatorState } from "./generator.state.ts";

export const BAY_LENGTHS: readonly number[] = [1050, 1250, 1655, 1964, 2500, 3050];
export const BAY_WIDTHS: readonly number[] = [700, 770, 1050, 1250, 1655, 1964, 2500, 3050];

const BAY_LIMITS = { min: 1, max: 12 };
const LIFT_LIMITS = { min: 1, max: 8 };

function clamp(value: number, limits: { min: number; max: number }): number {
  return Math.max(limits.min, Math.min(limits.max, value));
}

export function setNumBays(value: number): void {
  const numBays = clamp(value, BAY_LIMITS);
  const { hatchBay } = generatorState.get();

  generatorState.set({ numBays, hatchBay: hatchBay < numBays ? hatchBay : -1 });
}

export function setNumLifts(value: number): void {
  generatorState.set({ numLifts: clamp(value, LIFT_LIMITS) });
}

export function setBayLen(bayLen: number): void {
  generatorState.set({ bayLen });
}

export function setBayWidth(bayWidth: number): void {
  generatorState.set({ bayWidth });
}

export function setDeckMode(deckMode: DeckMode): void {
  generatorState.set({ deckMode });
}

export function setHatchBay(hatchBay: number): void {
  generatorState.set({ hatchBay });
}

export function setGuard(guard: boolean): void {
  generatorState.set({ guard });
}

export function setDiag(diag: boolean): void {
  generatorState.set({ diag });
}

/** Plank family affects what is already drawn, so it re-renders immediately. */
export function setPlankFam(plankFam: PlankFamily): void {
  generatorState.set({ plankFam });
  getEngine().setPlankFamily(plankFam);
}

export function setTerrain(terrain: TerrainKind): void {
  generatorState.set({ terrain });
  getEngine().setTerrain(terrain);
}

export function runGenerator(): void {
  const state = generatorState.get();

  getEngine().generate({
    numBays: state.numBays,
    numLifts: state.numLifts,
    bayLen: state.bayLen,
    bayWidth: state.bayWidth,
    deckMode: state.deckMode,
    plankFam: state.plankFam,
    hatchBay: state.hatchBay,
    guard: state.guard,
    diag: state.diag,
  });
  setHud(`Generated ${state.numBays} bays × ${state.numLifts} lifts`);
}

function currentSettings(state: GeneratorState): Record<string, unknown> {
  return {
    bayLen: state.bayLen,
    bayWidth: state.bayWidth,
    plankFam: state.plankFam,
    terrain: state.terrain,
    baseDatum: getBaseDatumMm(),
    planes: serializePlanes(),
  };
}

function settingsOf(raw: unknown): Record<string, unknown> {
  if (typeof raw !== "object" || raw === null || !("settings" in raw)) {
    return {};
  }

  const { settings } = raw;

  return typeof settings === "object" && settings !== null ? { ...settings } : {};
}

/**
 * The base datum shifts the whole node grid, so a design saved at 300 mm that
 * reopened at the default 55 mm would sit at the wrong height. Restore it
 * before the model loads; older files without the field keep the default. The
 * build planes come back the same way — a design without any has none.
 */
function restoreSettings(raw: unknown): void {
  const settings = settingsOf(raw);

  const { baseDatum } = settings;
  if (typeof baseDatum === "number" && Number.isFinite(baseDatum)) {
    setBaseDatum(baseDatum, false);
  }
  restorePlanes(settings.planes);
}

export function saveDesign(): void {
  const json = JSON.stringify(getEngine().serialize(currentSettings(generatorState.get())), null, 1);

  download(new Blob([json], { type: "application/json" }), "haki-scaffold.json");
}

/**
 * Export the scaffold alone as binary glTF, at its world coordinates — the
 * imported reference model and the ground are never included.
 */
export async function exportScaffoldGlb(): Promise<Result<null>> {
  const result = await getEngine().exportGlb();

  if (result.error) {
    setHud(result.error.msg);
    return { error: result.error };
  }
  if (!result.data) {
    return { data: null };
  }

  download(new Blob([result.data], { type: "model/gltf-binary" }), "haki-scaffold.glb");
  setHud("Scaffold exported as GLB at its world coordinates");

  return { data: null };
}

export async function openDesign(file: File): Promise<Result<null>> {
  let text: string;
  try {
    text = await file.text();
  } catch (err) {
    return fail("Could not read the file", err);
  }

  try {
    const raw: unknown = JSON.parse(text);
    restoreSettings(raw);
    getEngine().load(raw);
    setHud("Model loaded");
    return ok(null);
  } catch (err) {
    return fail("Could not read the file — it is not a valid design JSON", err);
  }
}
